import type { REALTIME_SUBSCRIBE_STATES, RealtimeChannel } from '@supabase/supabase-js';

import { supabase } from './supabase';

type ChatRealtimeScope = 'project' | 'workspace';

interface ChatSessionRealtimeOptions {
  accessToken: string;
  onChange: () => void;
  onStatus?: (connected: boolean) => void;
  scope: ChatRealtimeScope;
  sessionId: string;
}

interface TenantChatRealtimeOptions {
  accessToken: string;
  onChange: (payload?: unknown) => void;
  tenantId: string;
}

interface AgentRunRealtimeOptions {
  accessToken: string;
  onChange: () => void;
  onStatus?: (connected: boolean) => void;
  runId: string;
}

interface SkillExecutionRealtimeOptions {
  accessToken: string;
  executionId: string;
  onChange: () => void;
  onStatus?: (connected: boolean) => void;
}

interface TenantSkillExecutionsRealtimeOptions {
  accessToken: string;
  onChange: () => void;
  onStatus?: (connected: boolean) => void;
  tenantId: string;
}

let channelSequence = 0;

function connected(status: REALTIME_SUBSCRIBE_STATES): boolean {
  return status === 'SUBSCRIBED';
}

function subscribeAuthenticated(
  accessToken: string,
  channel: RealtimeChannel,
  onChange: () => void,
  onStatus?: (connected: boolean) => void
): () => void {
  let disposed = false;

  void supabase.realtime
    .setAuth(accessToken)
    .then(() => {
      if (disposed) return;
      channel.subscribe((status) => {
        if (disposed) return;
        const isConnected = connected(status);
        onStatus?.(isConnected);
        // A successful initial subscription or reconnect can follow a gap in
        // delivery. Re-read canonical REST state immediately instead of
        // waiting for the slow fallback poll or trusting a partial event log.
        if (isConnected) onChange();
      });
    })
    .catch(() => {
      if (!disposed) onStatus?.(false);
    });

  return () => {
    disposed = true;
    onStatus?.(false);
    void supabase.removeChannel(channel);
  };
}

/**
 * Pushes persisted chat changes over Supabase Realtime's WebSocket transport.
 * Payloads are deliberately ignored: the authenticated REST read remains the
 * canonical parser and prevents raw database records from becoming UI state.
 */
export function subscribeToChatSessionRealtime({
  accessToken,
  onChange,
  onStatus,
  scope,
  sessionId,
}: ChatSessionRealtimeOptions): () => void {
  const messageTable = scope === 'project' ? 'chat_messages' : 'workspace_chat_messages';
  const channel = supabase
    .channel(`chat:${scope}:${sessionId}:${++channelSequence}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        filter: `session_id=eq.${sessionId}`,
        schema: 'public',
        table: messageTable,
      },
      onChange
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        filter: `session_id=eq.${sessionId}`,
        schema: 'public',
        table: messageTable,
      },
      onChange
    );

  return subscribeAuthenticated(accessToken, channel, onChange, onStatus);
}

/**
 * Uses the append-only runtime ledger as a low-latency signal. Consumers still read the ordered,
 * validated event API with a sequence cursor instead of trusting raw database payloads.
 */
export function subscribeToAgentRunRealtime({
  accessToken,
  onChange,
  onStatus,
  runId,
}: AgentRunRealtimeOptions): () => void {
  const channel = supabase.channel(`agent-run:${runId}:${++channelSequence}`).on(
    'postgres_changes',
    {
      event: 'INSERT',
      filter: `run_id=eq.${runId}`,
      schema: 'public',
      table: 'agent_runtime_events',
    },
    onChange
  );
  return subscribeAuthenticated(accessToken, channel, onChange, onStatus);
}

/** Invalidates one project skill execution while its durable worker state changes. */
export function subscribeToSkillExecutionRealtime({
  accessToken,
  executionId,
  onChange,
  onStatus,
}: SkillExecutionRealtimeOptions): () => void {
  const channel = supabase.channel(`skill-execution:${executionId}:${++channelSequence}`).on(
    'postgres_changes',
    {
      event: 'UPDATE',
      filter: `id=eq.${executionId}`,
      schema: 'public',
      table: 'skill_executions',
    },
    onChange
  );
  return subscribeAuthenticated(accessToken, channel, onChange, onStatus);
}

/** Keeps tenant skill launch/history screens current across workers, tabs and agent runs. */
export function subscribeToTenantSkillExecutionsRealtime({
  accessToken,
  onChange,
  onStatus,
  tenantId,
}: TenantSkillExecutionsRealtimeOptions): () => void {
  const channel = supabase.channel(`skill-executions:${tenantId}:${++channelSequence}`);
  for (const event of ['INSERT', 'UPDATE'] as const) {
    channel.on(
      'postgres_changes',
      {
        event,
        filter: `tenant_id=eq.${tenantId}`,
        schema: 'public',
        table: 'skill_executions',
      },
      onChange
    );
  }
  return subscribeAuthenticated(accessToken, channel, onChange, onStatus);
}

/** Keeps the sidebar's project and workspace conversations current across tabs. */
export function subscribeToTenantChatRealtime({
  accessToken,
  onChange,
  tenantId,
}: TenantChatRealtimeOptions): () => void {
  const channel = supabase.channel(`chat-list:${tenantId}:${++channelSequence}`);
  for (const table of ['chat_sessions', 'workspace_chat_sessions']) {
    channel
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          filter: `tenant_id=eq.${tenantId}`,
          schema: 'public',
          table,
        },
        onChange
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          filter: `tenant_id=eq.${tenantId}`,
          schema: 'public',
          table,
        },
        onChange
      );
  }

  return subscribeAuthenticated(accessToken, channel, onChange);
}
